import React, { useEffect, useMemo, useRef, useState } from "react";
import { arc, geoAzimuthalEquidistant, geoGraticule, geoPath, select } from "d3";
import { feature } from "topojson-client";
import { normalizeDirectionKey, riskSeverity, type KoormaDirectionId, type RiskSeverity } from "./koormaUtils";

type RegionalRiskLike = {
  koorma_direction: string;
  risk_score_weighted: number;
  probability?: number;
  primary_threat?: string | null;
  top_industries?: string[];
  region?: string;
  mapped_modern_regions?: string[];
};

type KoormaAffliction = {
  koorma_direction?: string;
  nakshatra?: string;
  reason?: string;
  note?: string;
};

type KoormaWorldMapProps = {
  risks: RegionalRiskLike[];
  koormaAfflictions: KoormaAffliction[];
  className?: string;
};

type SectorData = {
  id: KoormaDirectionId;
  name: string;
  nameSanskrit: string;
  direction: string;
  nakshatras: string[];
  regions: string;
  element: string;
  color: string;
  colorFaint: string;
  angleStart: number | null;
  angleEnd: number | null;
};

const SECTORS: SectorData[] = [
  {
    id: "central",
    name: "Central",
    nameSanskrit: "मध्य",
    direction: "Central",
    nakshatras: ["Abhijit", "Shravana", "Rohini", "Ashvini", "Pushya"],
    regions: "Bhārata (India proper)",
    element: "Ākāśa",
    color: "#d4a853",
    colorFaint: "rgba(212,168,83,0.10)",
    angleStart: null,
    angleEnd: null,
  },
  {
    id: "east",
    name: "East",
    nameSanskrit: "पूर्व",
    direction: "East",
    nakshatras: ["Ardra", "Punarvasu", "Pushya"],
    regions: "Bengal, Myanmar, Southeast Asia",
    element: "Agni",
    color: "#c47a4a",
    colorFaint: "rgba(196,122,74,0.08)",
    angleStart: -22.5,
    angleEnd: 22.5,
  },
  {
    id: "southeast",
    name: "South-East",
    nameSanskrit: "आग्नेय",
    direction: "South-East",
    nakshatras: ["Ashlesha", "Magha", "Purva Phalguni"],
    regions: "Sri Lanka, Indonesia, Oceania",
    element: "Agni-Vāyu",
    color: "#d44a2e",
    colorFaint: "rgba(212,74,46,0.08)",
    angleStart: 22.5,
    angleEnd: 67.5,
  },
  {
    id: "south",
    name: "South",
    nameSanskrit: "दक्षिण",
    direction: "South",
    nakshatras: ["Uttara Phalguni", "Hasta", "Chitra"],
    regions: "Southern Ocean, Antarctica direction",
    element: "Pṛthvī",
    color: "#a03e2e",
    colorFaint: "rgba(160,62,46,0.08)",
    angleStart: 67.5,
    angleEnd: 112.5,
  },
  {
    id: "southwest",
    name: "South-West",
    nameSanskrit: "नैऋत्य",
    direction: "South-West",
    nakshatras: ["Swati", "Vishakha", "Anuradha"],
    regions: "East Africa, Madagascar, Southern Africa",
    element: "Rākṣasa",
    color: "#7a4a8a",
    colorFaint: "rgba(122,74,138,0.08)",
    angleStart: 112.5,
    angleEnd: 157.5,
  },
  {
    id: "west",
    name: "West",
    nameSanskrit: "पश्चिम",
    direction: "West",
    nakshatras: ["Jyeshtha", "Mula", "Purva Ashadha"],
    regions: "Arabia, Mediterranean, West Africa",
    element: "Varuṇa",
    color: "#4a5cb5",
    colorFaint: "rgba(74,92,181,0.08)",
    angleStart: 157.5,
    angleEnd: 202.5,
  },
  {
    id: "northwest",
    name: "North-West",
    nameSanskrit: "वायव्य",
    direction: "North-West",
    nakshatras: ["Uttara Ashadha", "Shravana", "Dhanishta"],
    regions: "Afghanistan, Iran, Central Asia",
    element: "Vāyu",
    color: "#3a9e8f",
    colorFaint: "rgba(58,158,143,0.08)",
    angleStart: 202.5,
    angleEnd: 247.5,
  },
  {
    id: "north",
    name: "North",
    nameSanskrit: "उत्तर",
    direction: "North",
    nakshatras: ["Shatabhisha", "Purva Bhadrapada", "Uttara Bhadrapada"],
    regions: "Himalayas, Tibet, Mongolia, Siberia",
    element: "Soma",
    color: "#5aaa6e",
    colorFaint: "rgba(90,170,110,0.08)",
    angleStart: 247.5,
    angleEnd: 292.5,
  },
  {
    id: "northeast",
    name: "North-East",
    nameSanskrit: "ईशान",
    direction: "North-East",
    nakshatras: ["Revati", "Ashvini", "Bharani"],
    regions: "China, Japan, East Asia",
    element: "Īśāna",
    color: "#6ab5d4",
    colorFaint: "rgba(106,181,212,0.08)",
    angleStart: -67.5,
    angleEnd: -22.5,
  },
];

const INDIA_CENTER: [number, number] = [78.9, 22.5];
const MAP_OVERLAY_ROTATION_DEG = 90;
const WORLD_ATLAS_URLS = ["/countries-110m.json", "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json"];

let cachedWorldAtlas: any | null = null;
let cachedWorldAtlasPromise: Promise<any> | null = null;

async function getWorldAtlas(signal: AbortSignal) {
  if (cachedWorldAtlas) return cachedWorldAtlas;
  if (!cachedWorldAtlasPromise) {
    cachedWorldAtlasPromise = (async () => {
      let lastError: unknown = null;
      for (const url of WORLD_ATLAS_URLS) {
        try {
          const r = await fetch(url, { signal });
          if (!r.ok) {
            lastError = new Error(`Failed to load world topology (${r.status})`);
            continue;
          }
          return r.json();
        } catch (e) {
          lastError = e;
        }
      }
      throw lastError instanceof Error ? lastError : new Error("Failed to load world topology");
    })();
  }
  cachedWorldAtlas = await cachedWorldAtlasPromise;
  return cachedWorldAtlas;
}

function opacityForSeverity(sev: RiskSeverity) {
  if (sev === "high") return 0.22;
  if (sev === "medium") return 0.16;
  return 0.12;
}

function baseOpacityForSector(id: KoormaDirectionId) {
  return id === "central" ? 0.06 : 0.08;
}

export default function KoormaWorldMap({ risks, koormaAfflictions, className }: KoormaWorldMapProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const sectorElsRef = useRef<Map<KoormaDirectionId, SVGElement>>(new Map());

  const [width, setWidth] = useState(0);
  const [hoveredId, setHoveredId] = useState<KoormaDirectionId | null>(null);
  const [selectedId, setSelectedId] = useState<KoormaDirectionId | null>(null);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!wrapperRef.current) return;
    const el = wrapperRef.current;
    const ro = new ResizeObserver(() => {
      setWidth(el.clientWidth);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const riskBySectorId = useMemo(() => {
    const map = new Map<KoormaDirectionId, { severity: RiskSeverity; risk: RegionalRiskLike }>();
    for (const r of risks || []) {
      const key = normalizeDirectionKey(r?.koorma_direction || "");
      const found = SECTORS.find((s) => normalizeDirectionKey(s.direction) === key);
      if (!found) continue;
      map.set(found.id, { severity: riskSeverity(Number(r?.risk_score_weighted || 0)), risk: r });
    }
    return map;
  }, [risks]);

  const afflictionsBySectorId = useMemo(() => {
    const map = new Map<KoormaDirectionId, { nakshatras: Set<string> }>();
    for (const a of koormaAfflictions || []) {
      const key = normalizeDirectionKey(a?.koorma_direction || "");
      const found = SECTORS.find((s) => normalizeDirectionKey(s.direction) === key);
      if (!found) continue;
      const nak = typeof a?.nakshatra === "string" ? a.nakshatra.trim() : "";
      if (!nak) continue;
      if (!map.has(found.id)) map.set(found.id, { nakshatras: new Set() });
      map.get(found.id)!.nakshatras.add(nak);
    }
    return map;
  }, [koormaAfflictions]);

  const activeSector = hoveredId || selectedId;
  const activeSectorData = useMemo(() => (activeSector ? SECTORS.find((s) => s.id === activeSector) ?? null : null), [activeSector]);
  const activeRisk = useMemo(() => (activeSector ? riskBySectorId.get(activeSector) ?? null : null), [activeSector, riskBySectorId]);
  const activeAfflictions = useMemo(() => (activeSector ? afflictionsBySectorId.get(activeSector) ?? null : null), [activeSector, afflictionsBySectorId]);

  useEffect(() => {
    if (!svgRef.current) return;
    if (!width) return;

    const svgEl = svgRef.current;
    const svg = select(svgEl);
    svg.selectAll("*").remove();
    sectorElsRef.current.clear();
    setError("");

    const height = Math.min(width * 0.56, 720);

    svg.attr("viewBox", `0 0 ${width} ${height}`).attr("preserveAspectRatio", "xMidYMid meet");

    const defs = svg.append("defs");

    const radGrad = defs.append("radialGradient").attr("id", "koorma-vignette").attr("cx", "50%").attr("cy", "50%").attr("r", "55%");
    radGrad.append("stop").attr("offset", "0%").attr("stop-color", "transparent");
    radGrad.append("stop").attr("offset", "85%").attr("stop-color", "transparent");
    radGrad.append("stop").attr("offset", "100%").attr("stop-color", "rgba(8,10,16,0.7)");

    const glow = defs.append("filter").attr("id", "koorma-glow").attr("x", "-50%").attr("y", "-50%").attr("width", "200%").attr("height", "200%");
    glow.append("feGaussianBlur").attr("stdDeviation", "3").attr("result", "blur");
    glow.append("feMerge")
      .selectAll("feMergeNode")
      .data(["blur", "SourceGraphic"])
      .enter()
      .append("feMergeNode")
      .attr("in", (d) => d);

    // Diagonal stripe patterns for top-3 threat sectors
    const threatColors = ["#dc2626", "#ea580c", "#d97706"]; // red, orange, amber
    const threatStrokeWidths = [3.0, 2.5, 2.0];
    threatColors.forEach((color, i) => {
      const pat = defs.append("pattern")
        .attr("id", `koorma-threat-stripe-${i}`)
        .attr("patternUnits", "userSpaceOnUse")
        .attr("width", 10)
        .attr("height", 10)
        .attr("patternTransform", "rotate(45)");
      pat.append("line")
        .attr("x1", 0).attr("y1", 0)
        .attr("x2", 0).attr("y2", 10)
        .attr("stroke", color)
        .attr("stroke-width", threatStrokeWidths[i])
        .attr("stroke-opacity", 0.7);
    });

    const projection = geoAzimuthalEquidistant()
      .center([0, 0])
      .rotate([-INDIA_CENTER[0], -INDIA_CENTER[1]])
      .translate([width / 2, height / 2])
      .scale(Math.min(width, height) * 0.37)
      .clipAngle(150);

    const path = geoPath().projection(projection);

    const cx = width / 2;
    const cy = height / 2;
    const maxR = Math.min(width, height) * 0.48;
    const centerR = maxR * 0.12;

    svg.append("circle").attr("cx", cx).attr("cy", cy).attr("r", maxR).attr("fill", "#0b0f18");

    const gr = geoGraticule().step([15, 15]);
    svg.append("path").datum(gr()).attr("d", path).attr("fill", "none").attr("stroke", "rgba(255,255,255,0.03)").attr("stroke-width", 0.3);

    const abort = new AbortController();
    let cancelled = false;
    void (async () => {
      try {
        const world = await getWorldAtlas(abort.signal);
        if (cancelled) return;
        const countries = feature(world, world.objects.countries as any) as any;

        const countryPaths = svg.append("g");
        countryPaths
          .selectAll("path")
          .data(countries.features)
          .enter()
          .append("path")
          .attr("d", path as any)
          .attr("fill", (d: any) => (d?.id === "356" ? "#2a3555" : "#1a2030"))
          .attr("stroke", (d: any) => (d?.id === "356" ? "none" : "#2a3448"))
          .attr("stroke-width", 0.4);

        const cashmirGroup = svg.append("g");

        const pokGilgit = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [74.8, 37.1],
                [75.0, 37.15],
                [75.3, 37.1],
                [75.6, 37.05],
                [75.8, 36.95],
                [76.1, 36.8],
                [76.3, 36.6],
                [76.5, 36.5],
                [76.8, 36.4],
                [77.0, 36.2],
                [76.5, 36.0],
                [76.2, 35.8],
                [76.0, 35.6],
                [75.8, 35.4],
                [75.6, 35.2],
                [75.4, 35.0],
                [75.2, 34.8],
                [75.0, 34.6],
                [74.8, 34.5],
                [74.6, 34.3],
                [74.4, 34.2],
                [74.2, 34.05],
                [74.0, 33.9],
                [73.8, 33.75],
                [73.6, 33.7],
                [73.4, 33.65],
                [73.2, 33.7],
                [73.05, 33.8],
                [72.9, 34.0],
                [72.8, 34.3],
                [72.75, 34.5],
                [72.8, 34.7],
                [72.85, 35.0],
                [72.9, 35.2],
                [72.85, 35.5],
                [72.8, 35.7],
                [72.85, 36.0],
                [72.9, 36.3],
                [73.0, 36.5],
                [73.1, 36.7],
                [73.3, 36.85],
                [73.6, 36.9],
                [73.9, 36.95],
                [74.2, 37.0],
                [74.5, 37.05],
                [74.8, 37.1],
              ],
            ],
          },
        };

        const aksaiChin = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "Polygon",
            coordinates: [
              [
                [77.0, 36.2],
                [77.5, 36.1],
                [78.0, 36.0],
                [78.5, 35.9],
                [79.0, 35.7],
                [79.3, 35.5],
                [79.6, 35.3],
                [79.8, 35.1],
                [80.0, 34.9],
                [80.2, 34.7],
                [80.3, 34.5],
                [80.3, 34.3],
                [80.2, 34.1],
                [80.1, 33.9],
                [79.9, 33.7],
                [79.7, 33.5],
                [79.5, 33.3],
                [79.2, 33.2],
                [78.8, 33.1],
                [78.5, 33.1],
                [78.2, 33.15],
                [77.9, 33.2],
                [77.6, 33.3],
                [77.3, 33.5],
                [77.0, 33.7],
                [76.8, 33.9],
                [76.6, 34.2],
                [76.5, 34.5],
                [76.4, 34.8],
                [76.3, 35.1],
                [76.2, 35.4],
                [76.2, 35.7],
                [76.3, 36.0],
                [76.5, 36.0],
                [76.8, 36.1],
                [77.0, 36.2],
              ],
            ],
          },
        };

        for (const territory of [pokGilgit, aksaiChin]) {
          cashmirGroup
            .append("path")
            .datum(territory as any)
            .attr("d", path as any)
            .attr("fill", "#2a3555")
            .attr("stroke", "#2a3555")
            .attr("stroke-width", 2.5)
            .attr("stroke-linejoin", "round");
        }

        const unifiedOuterBorder = {
          type: "Feature",
          properties: {},
          geometry: {
            type: "MultiLineString",
            coordinates: [
              [
                [73.05, 33.8],
                [72.9, 34.0],
                [72.8, 34.3],
                [72.75, 34.5],
                [72.8, 34.7],
                [72.85, 35.0],
                [72.9, 35.2],
                [72.85, 35.5],
                [72.8, 35.7],
                [72.85, 36.0],
                [72.9, 36.3],
                [73.0, 36.5],
                [73.1, 36.7],
                [73.3, 36.85],
                [73.6, 36.9],
                [73.9, 36.95],
                [74.2, 37.0],
                [74.5, 37.05],
                [74.8, 37.1],
                [75.0, 37.15],
                [75.3, 37.1],
                [75.6, 37.05],
                [75.8, 36.95],
                [76.1, 36.8],
                [76.3, 36.6],
                [76.5, 36.5],
                [76.8, 36.4],
                [77.0, 36.2],
              ],
              [
                [77.0, 36.2],
                [77.5, 36.1],
                [78.0, 36.0],
                [78.5, 35.9],
                [79.0, 35.7],
                [79.3, 35.5],
                [79.6, 35.3],
                [79.8, 35.1],
                [80.0, 34.9],
                [80.2, 34.7],
                [80.3, 34.5],
                [80.3, 34.3],
                [80.2, 34.1],
                [80.1, 33.9],
                [79.9, 33.7],
                [79.7, 33.5],
                [79.5, 33.3],
                [79.2, 33.2],
                [78.8, 33.1],
                [78.5, 33.1],
                [78.2, 33.15],
                [77.9, 33.2],
                [77.6, 33.3],
                [77.3, 33.5],
                [77.0, 33.7],
              ],
            ],
          },
        };

        cashmirGroup
          .append("path")
          .datum(unifiedOuterBorder as any)
          .attr("d", path as any)
          .attr("fill", "none")
          .attr("stroke", "#8a6d2f")
          .attr("stroke-width", 0.8);

        const indiaFeature = countries.features.find((f: any) => f?.id === "356");
        if (indiaFeature) {
          cashmirGroup
            .append("path")
            .datum(indiaFeature as any)
            .attr("d", path as any)
            .attr("fill", "none")
            .attr("stroke", "#8a6d2f")
            .attr("stroke-width", 0.8);
        }

        svg.append("circle").attr("cx", cx).attr("cy", cy).attr("r", maxR).attr("fill", "none").attr("stroke", "rgba(212, 168, 83, 0.15)").attr("stroke-width", 1.5);

        const sectorGroup = svg.append("g");
        const directionalSectors = SECTORS.filter((s) => s.angleStart !== null && s.angleEnd !== null);

        for (const sector of directionalSectors) {
          const rotatedStart = (sector.angleStart as number) + MAP_OVERLAY_ROTATION_DEG;
          const rotatedEnd = (sector.angleEnd as number) + MAP_OVERLAY_ROTATION_DEG;
          const a1 = (rotatedStart - 90) * (Math.PI / 180);

          const arcPath = arc()
            .innerRadius(centerR)
            .outerRadius(maxR)
            .startAngle(rotatedStart * (Math.PI / 180))
            .endAngle(rotatedEnd * (Math.PI / 180));

          const el = sectorGroup
            .append("path")
            .attr("d", arcPath as any)
            .attr("transform", `translate(${cx},${cy})`)
            .attr("fill", sector.color)
            .attr("opacity", baseOpacityForSector(sector.id))
            .attr("data-sector", sector.id)
            .style("cursor", "pointer")
            .on("mouseenter", () => {
              setHoveredId(sector.id);
            })
            .on("mouseleave", () => {
              setHoveredId(null);
            })
            .on("click", () => {
              setSelectedId((prev) => (prev === sector.id ? null : sector.id));
            });

          sectorElsRef.current.set(sector.id, (el.node() as SVGElement) || (null as any));

          const x1 = cx + Math.cos(a1) * centerR;
          const y1 = cy + Math.sin(a1) * centerR;
          const x2 = cx + Math.cos(a1) * maxR;
          const y2 = cy + Math.sin(a1) * maxR;
          sectorGroup
            .append("line")
            .attr("x1", x1)
            .attr("y1", y1)
            .attr("x2", x2)
            .attr("y2", y2)
            .attr("stroke", "rgba(212, 168, 83, 0.35)")
            .attr("stroke-width", 1)
            .attr("stroke-dasharray", "6 4");

          const midAngle = ((((sector.angleStart as number) + (sector.angleEnd as number)) / 2) + MAP_OVERLAY_ROTATION_DEG - 90) * (Math.PI / 180);
          const labelR = maxR * 0.78;
          const lx = cx + Math.cos(midAngle) * labelR;
          const ly = cy + Math.sin(midAngle) * labelR;

          sectorGroup
            .append("text")
            .attr("x", lx)
            .attr("y", ly)
            .attr("fill", "#d4a853")
            .attr("text-anchor", "middle")
            .attr("font-family", "Rajdhani, sans-serif")
            .attr("font-weight", 600)
            .attr("font-size", 11)
            .attr("letter-spacing", "0.12em")
            .attr("opacity", 0.85)
            .style("pointer-events", "none")
            .text(sector.direction.toUpperCase());

          sectorGroup
            .append("text")
            .attr("x", lx)
            .attr("y", ly + 13)
            .attr("fill", "#b8923e")
            .attr("text-anchor", "middle")
            .attr("font-family", '"Noto Sans Devanagari", sans-serif')
            .attr("font-weight", 400)
            .attr("font-size", 10)
            .attr("opacity", 0.6)
            .style("pointer-events", "none")
            .text(sector.nameSanskrit);

          const nakR = maxR * 0.60;
          const nx = cx + Math.cos(midAngle) * nakR;
          const ny = cy + Math.sin(midAngle) * nakR;
          sectorGroup
            .append("text")
            .attr("x", nx)
            .attr("y", ny)
            .attr("fill", "#9a917f")
            .attr("text-anchor", "middle")
            .attr("font-family", '"Cormorant Garamond", serif')
            .attr("font-weight", 300)
            .attr("font-style", "italic")
            .attr("font-size", 8.5)
            .attr("opacity", 0.7)
            .style("pointer-events", "none")
            .text(sector.nakshatras.join(" · "));
        }

        sectorGroup
          .append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", centerR)
          .attr("fill", "rgba(212,168,83,0.04)")
          .attr("stroke", "rgba(212,168,83,0.3)")
          .attr("stroke-width", 1)
          .attr("stroke-dasharray", "4 3")
          .style("cursor", "pointer")
          .on("mouseenter", () => setHoveredId("central"))
          .on("mouseleave", () => setHoveredId(null))
          .on("click", () => setSelectedId((prev) => (prev === "central" ? null : "central")));
        const centralEl = sectorGroup
          .append("circle")
          .attr("cx", cx)
          .attr("cy", cy)
          .attr("r", centerR)
          .attr("fill", SECTORS.find((s) => s.id === "central")?.color || "#d4a853")
          .attr("opacity", baseOpacityForSector("central"))
          .style("pointer-events", "none");
        sectorElsRef.current.set("central", (centralEl.node() as SVGElement) || (null as any));

        sectorGroup
          .append("text")
          .attr("x", cx)
          .attr("y", cy - 6)
          .attr("fill", "#d4a853")
          .attr("text-anchor", "middle")
          .attr("font-family", "Rajdhani, sans-serif")
          .attr("font-weight", 600)
          .attr("font-size", 10)
          .attr("letter-spacing", "0.12em")
          .attr("opacity", 0.85)
          .style("pointer-events", "none")
          .text("CENTRAL");

        sectorGroup
          .append("text")
          .attr("x", cx)
          .attr("y", cy + 8)
          .attr("fill", "#b8923e")
          .attr("text-anchor", "middle")
          .attr("font-family", '"Noto Sans Devanagari", sans-serif')
          .attr("font-weight", 400)
          .attr("font-size", 10)
          .attr("opacity", 0.6)
          .style("pointer-events", "none")
          .text("मध्य");

        const indiaPos = projection(INDIA_CENTER);
        if (indiaPos) {
          svg.append("circle").attr("cx", indiaPos[0]).attr("cy", indiaPos[1]).attr("r", 3).attr("fill", "#d4a853").attr("filter", "drop-shadow(0 0 6px rgba(212, 168, 83, 0.6))");
          svg.append("circle").attr("cx", indiaPos[0]).attr("cy", indiaPos[1]).attr("r", 4).attr("fill", "none").attr("stroke", "#d4a853").attr("opacity", 0.3);
        }

        svg.append("circle").attr("cx", cx).attr("cy", cy).attr("r", maxR + 5).attr("fill", "url(#koorma-vignette)").attr("pointer-events", "none");

      } catch (e: any) {
        if (abort.signal.aborted) return;
        setError(e?.message || "Failed to load map data");
      }
    })();

    return () => {
      cancelled = true;
      abort.abort();
      svg.selectAll("*").remove();
      sectorElsRef.current.clear();
    };
  }, [width]);

  // Rank sectors by risk score to identify top 3 for prominent threat coloring
  const top3SectorIds = useMemo(() => {
    const entries = [...riskBySectorId.entries()]
      .map(([id, data]) => ({ id, score: Number(data.risk?.risk_score_weighted || 0) }))
      .filter((e) => e.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    return new Map(entries.map((e, i) => [e.id, i])); // id → rank (0=top, 1=2nd, 2=3rd)
  }, [riskBySectorId]);

  useEffect(() => {
    const hovered = hoveredId || selectedId;
    for (const [id, el] of sectorElsRef.current.entries()) {
      const risk = riskBySectorId.get(id);
      const sev = risk?.severity ?? null;
      const isHovered = hovered === id;
      const threatRank = top3SectorIds.get(id); // 0, 1, 2, or undefined

      // Top 3 threat sectors get diagonal stripe overlay + bold border
      if (threatRank !== undefined) {
        const threatBorderColors = ["#dc2626", "#ea580c", "#d97706"]; // red, orange, amber
        el.setAttribute("fill", `url(#koorma-threat-stripe-${threatRank})`);
        el.setAttribute("opacity", isHovered ? "0.95" : "0.85");
        el.setAttribute("stroke", threatBorderColors[threatRank]);
        el.setAttribute("stroke-width", threatRank === 0 ? "2.5" : "2");
        el.setAttribute("filter", "url(#koorma-glow)");
      } else {
        // Non-top-3 sectors: restore original sector color and subtle styling
        const sector = SECTORS.find((s) => s.id === id);
        if (sector) el.setAttribute("fill", sector.color);
        const opacity = isHovered ? 0.18 : sev ? opacityForSeverity(sev) : baseOpacityForSector(id);
        el.setAttribute("opacity", String(opacity));

        if (sev === "high") {
          el.setAttribute("stroke", "rgba(212, 74, 46, 0.9)");
          el.setAttribute("stroke-width", "2.2");
          el.setAttribute("filter", "url(#koorma-glow)");
        } else if (sev === "medium") {
          el.setAttribute("stroke", "rgba(212, 168, 83, 0.85)");
          el.setAttribute("stroke-width", "1.6");
          el.setAttribute("filter", "url(#koorma-glow)");
        } else if (sev === "low") {
          el.setAttribute("stroke", "rgba(58, 158, 143, 0.75)");
          el.setAttribute("stroke-width", "1.2");
          el.removeAttribute("filter");
        } else {
          el.setAttribute("stroke", "rgba(212, 168, 83, 0.30)");
          el.setAttribute("stroke-width", "1");
          el.removeAttribute("filter");
        }
      }
    }
  }, [hoveredId, riskBySectorId, selectedId, top3SectorIds]);

  const legendSectors = useMemo(() => SECTORS, []);

  return (
    <div ref={wrapperRef} className={className ? `${className} min-w-0` : "min-w-0"}>
      <div className="koorma-worldmap overflow-hidden rounded-lg border border-yellow-500/15 bg-slate-950/70">
        <div className="px-4 pt-4 text-center">
          <div className="font-[Cormorant_Garamond,serif] text-xl font-light tracking-[0.18em] text-yellow-400">Koorma Chakra</div>
          <div className="font-[Noto_Sans_Devanagari,sans-serif] text-sm tracking-[0.3em] text-yellow-500/70">कूर्म चक्र</div>
          <div className="mt-2 text-[11px] font-light tracking-[0.25em] text-slate-400">Varāhamihira’s geospatial mundane framework</div>
        </div>

        <div className="relative mt-4">
          {error ? (
            <div className="px-4 py-10 text-center text-sm text-rose-300">{error}</div>
          ) : (
            <svg ref={svgRef} className="block h-auto w-full" />
          )}
        </div>

        <div className="grid gap-4 p-4 md:grid-cols-2">
          <div className="min-w-0 rounded-lg border border-yellow-500/10 bg-slate-950/60 p-4">
            <div className="font-[Cormorant_Garamond,serif] text-sm font-semibold tracking-[0.15em] text-yellow-400">Directional Sectors</div>
            <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
              {legendSectors.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className="flex items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-yellow-500/5"
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => setSelectedId((prev) => (prev === s.id ? null : s.id))}
                >
                  <span className="h-3 w-3 shrink-0 rounded-sm border border-white/10" style={{ background: s.color }} />
                  <span className="text-[12px] text-slate-200">
                    <span className="block text-[12px] font-semibold text-slate-100">{s.direction}</span>
                    <span className="block font-[Noto_Sans_Devanagari,sans-serif] text-[11px] text-yellow-500/70">{s.nameSanskrit}</span>
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-3 border-t border-yellow-500/10 pt-3">
              <div className="text-[11px] font-semibold text-slate-400 mb-2">Threat Intensity</div>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="flex items-center gap-1.5 text-[11px] text-slate-300"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#dc2626", opacity: 0.9 }} />Highest</span>
                <span className="flex items-center gap-1.5 text-[11px] text-slate-300"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#ea580c", opacity: 0.9 }} />2nd</span>
                <span className="flex items-center gap-1.5 text-[11px] text-slate-300"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: "#d97706", opacity: 0.9 }} />3rd</span>
                <span className="flex items-center gap-1.5 text-[11px] text-slate-400"><span className="h-2.5 w-2.5 rounded-sm border border-white/10" style={{ background: "#3b82f6", opacity: 0.3 }} />Low / None</span>
              </div>
            </div>
          </div>

          <div className="min-w-0 rounded-lg border border-yellow-500/10 bg-slate-950/60 p-4">
            <div className="font-[Cormorant_Garamond,serif] text-sm font-semibold tracking-[0.15em] text-yellow-400">Sector Detail</div>
            {activeSectorData ? (
              <div className="mt-3 text-sm text-slate-300">
                <div className="text-lg font-semibold text-yellow-400">{activeSectorData.name}</div>
                <div className="font-[Noto_Sans_Devanagari,sans-serif] text-sm text-yellow-500/70">{activeSectorData.nameSanskrit}</div>

                <div className="mt-3 space-y-2 text-[13px]">
                  <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
                    <span className="text-slate-500">Direction</span>
                    <span className="min-w-0 break-words text-right text-slate-100">{activeSectorData.direction}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
                    <span className="text-slate-500">Nakshatras</span>
                    <span className="min-w-0 break-words text-right text-slate-100">{activeSectorData.nakshatras.join(", ")}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
                    <span className="text-slate-500">Mapped Region</span>
                    <span className="min-w-0 break-words text-right text-slate-100">{activeSectorData.regions}</span>
                  </div>
                  <div className="flex items-start justify-between gap-3 border-b border-white/5 pb-2">
                    <span className="text-slate-500">Tattva</span>
                    <span className="min-w-0 break-words text-right text-slate-100">{activeSectorData.element}</span>
                  </div>

                  {activeRisk ? (
                    <div className="rounded-md border border-yellow-500/10 bg-slate-900/30 px-3 py-2">
                      <div className="flex items-start justify-between gap-3">
                        <span className="text-slate-400">Risk</span>
                        <span className="min-w-0 break-words text-right text-slate-100">
                          {Number(activeRisk.risk.risk_score_weighted || 0).toFixed(1)} ({activeRisk.severity})
                        </span>
                      </div>
                      {activeRisk.risk.primary_threat ? <div className="mt-1 break-words text-[12px] text-rose-300">Threat: {activeRisk.risk.primary_threat}</div> : null}
                      {Array.isArray(activeRisk.risk.top_industries) && activeRisk.risk.top_industries.length ? (
                        <div className="mt-1 break-words text-[12px] text-slate-400">Industries: {activeRisk.risk.top_industries.join(", ")}</div>
                      ) : null}
                    </div>
                  ) : null}

                  {activeAfflictions?.nakshatras?.size ? (
                    <div className="rounded-md border border-yellow-500/10 bg-slate-900/30 px-3 py-2">
                      <div className="text-slate-400">Affliction Nakshatras</div>
                      <div className="mt-1 break-words text-[12px] text-slate-100">{[...activeAfflictions.nakshatras].join(", ")}</div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="mt-3 text-sm text-slate-500">Hover or tap a sector to view details.</div>
            )}
          </div>
        </div>

        <div className="px-4 pb-4 text-center text-[11px] text-slate-500">
          Directional sectors follow the Koorma Chakra convention — an India-centred cosmological reference frame. They are not compass bearings from any queried location.
        </div>
      </div>
    </div>
  );
}
